import React, { useEffect, useState, useRef } from "react";
import { Spin } from 'antd';
import { useParams } from 'react-router-dom';
import { getCookie, getUserInfo, is_first_time_user, list_user_workspaces } from "../backendApi/backend";

import StandartLayout from '../layouts/StandartLayout';
import StarterMessages, { StarterMessageItem } from '../components/StarterMessages';
import {
  ChatMessage,
  ChatInputControls,
  FilePreview,
  ImagePreview,
  ToolStatusIndicator,
  WelcomeBanner,
  FirstTimeModal
} from '../components';

import { useChat, useFileHandling } from '../hooks';

import { extractAllImages, extractAllVideos, extractAllFiles, removeMediaUrls, onDownload } from '../utils';

import './Home.css';


function Home() {
  const { uuid } = useParams<{ uuid: string }>();
  
  // Chat hook
  const {
    input,
    setInput,
    selectedImage,
    setSelectedImage,
    selectedFile,
    setSelectedFile,
    imagePreviewUrl,
    setImagePreviewUrl,
    filePreviewInfo,
    setFilePreviewInfo,
    showStarterMessages,
    isUploading,
    messages,
    toolName,
    toolStatus,
    isFocused,
    setIsFocused,
    loadingStarterMessageId,
    setLoadingStarterMessageId,
    inputRef,
    isToolRunningRef,
    handleSend,
    handleStarterMessageClick,
    handleKeyPress,
    handleInputFocus,
    isWaitingForResponse,
  } = useChat(uuid);

  // File handling hook
  const { handleFileChange, removeImage, removeFile } = useFileHandling(
    setSelectedImage,
    setSelectedFile,
    setImagePreviewUrl,
    setFilePreviewInfo,
    (uploading: boolean) => {} // isUploading is managed by useChat
  );

  // Local state for notifications and user info
  const [isLoadingUserInfo, setIsLoadingUserInfo] = useState<boolean>(true);
  const [activatedEmail, setActivatedEmail] = useState<boolean | undefined>(undefined);
  const [firstLogin, setFirstLogin] = useState<boolean>(false);
  const [showFirstTimeModal, setShowFirstTimeModal] = useState<boolean>(false);

  const [shouldRedirectRandomly] = useState<boolean>(() => {
    return Math.random() < 0.2;
  });

  // Define starter messages
  const starterMessageItems: StarterMessageItem[] = [
    {
      id: 'greeting',
      icon: '👋',
      text: 'Hi!'
    },
    {
      id: 'help-options',
      icon: '❓',
      text: 'How you can help me?'
    }
  ];

  useEffect(() => {
    const fetchUserInfo = async () => {
      setIsLoadingUserInfo(true);
      try {
        const response = await getUserInfo();
        setActivatedEmail(response.updatedProfile?.activated_email);
      } catch (error) {
        console.error("Error fetching user info:", error);
        setActivatedEmail(true); 
      } finally {
        setIsLoadingUserInfo(false);
      }
    };
    
    fetchUserInfo();
    is_first_time_user().then((response) => {
      if (response.is_first_time_user) {
        list_user_workspaces().then((workspaces) => {
          
          if (workspaces.length === 0) {
            setFirstLogin(true);
            setShowFirstTimeModal(true);
          }
        });
      }
    }).catch((error) => {
      console.error("Error checking first time user:", error);
    });

    // from storage check if workspaceid is set
    if (window.localStorage.getItem("currentWorkspaceId") === undefined) {
      setShowFirstTimeModal(true);
    }

  }, []);

  const handleCloseFirstTimeModal = () => {
    setShowFirstTimeModal(false);
    setFirstLogin(false);
  };

  const handleWorkspaceCreated = (workspaceData: any) => {
    console.log('Workspace created:', workspaceData);
    // Optionally refresh the page or update other state
  };

  // Function to scroll to bottom
  const scrollToBottom = () => {
      window.scrollTo(0, document.body.scrollHeight);   
  };

  // Enhanced handleSend function with scroll
  const handleSendWithScroll = () => {
    handleSend();
    // Multiple scroll attempts with different timings
    scrollToBottom();
  };

  // Enhanced handleStarterMessageClick with scroll
  const handleStarterMessageClickWithScroll = (messageId: string) => {
    handleStarterMessageClick(messageId);
    scrollToBottom();
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <StandartLayout>
      <FirstTimeModal
        isOpen={showFirstTimeModal}
        onClose={handleCloseFirstTimeModal}
        onWorkspaceCreated={handleWorkspaceCreated}
      />
      
      <div className="chat-container">
        {messages.length > 0 && (
          <div className="chat-box">
            {messages.map((message, index) => (
              <ChatMessage
                key={index}
                message={message}
                index={index}
                onDownload={onDownload}
                extractAllImages={extractAllImages}
                extractAllVideos={extractAllVideos}
                extractAllFiles={extractAllFiles}
                removeMediaUrls={removeMediaUrls}
              />
            ))}
            {isWaitingForResponse && (
              <div className="thinking-indicator">
                <Spin size="small" />
                <span>Thinking...</span>
              </div>
            )}
            <ToolStatusIndicator
              isToolRunning={isToolRunningRef.current}
              toolName={toolName}
              toolStatus={toolStatus}
            />
          </div>
        )}


        {showStarterMessages && messages.length === 0 && (
          <div className="starter-messages-wrapper">
            <WelcomeBanner showStarterMessages={showStarterMessages && messages.length === 0} />
            <StarterMessages 
              messages={starterMessageItems}
              onMessageClick={handleStarterMessageClickWithScroll}
              loadingMessageId={loadingStarterMessageId}
            />
          </div>
        )}

        <div className="chat-input-container">
          {imagePreviewUrl && (
            <ImagePreview
              imageUrl={imagePreviewUrl}
              isUploading={isUploading}
              onRemove={removeImage}
            />
          )}

          {filePreviewInfo && (
            <FilePreview
              fileInfo={filePreviewInfo}
              isUploading={isUploading}
              onRemove={removeFile}
            />
          )}
        
          <div
            contentEditable
            ref={inputRef}
            onInput={(e) => setInput(e.currentTarget.textContent || "")}
            onFocus={handleInputFocus}
            onBlur={() => setIsFocused(false)}
            aria-placeholder="Type a message..."
            onKeyDown={handleKeyPress}
            className={`chat-input ${isToolRunningRef.current ? 'chat-input-disabled' : ''}`}
            autoCorrect="off"
            style={{ whiteSpace: 'pre-wrap' }}
          />

          <input
            id="file-upload"
            type="file"
            accept=".pdf,.docx,.csv,.rtf,.txt,application/pdf,text/csv,application/rtf,text/plain"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          <ChatInputControls
            isUploading={isUploading}
            isToolRunning={isToolRunningRef.current}
            onFileUpload={() => document.getElementById('file-upload')?.click()}
            onSend={handleSendWithScroll}
          />
        </div>
      </div>
    </StandartLayout>
  );
}

export default Home;